import { CdkMenuModule, CdkMenuTrigger } from '@angular/cdk/menu';
import { NgClass } from '@angular/common';
import { Component, computed, inject, input, viewChild } from '@angular/core';
import { RouterLink } from '@angular/router';
import { menuTriggerAnimation } from '@core/animations/menu-trigger.animation';
import { MenuStructureService } from '@core/layouts/menu-structure.service';
import { NotificationPanelComponent } from '@components/notification/components/notification-panel/notification-panel.component';
import { NotificationStateService } from '@components/notification/services/notification-state.service';
import { Store } from '@ngrx/store';
import { FeatureFlagId } from '@api-types/enums/feature-flag-id.enum';
import { isFeatureFlagEnabled } from '@core/store/feature-flags/feature-flags.selectors';
import { TranslateModule } from '@ngx-translate/core';
import { LucideBell } from '@lucide/angular';
import { InlineSVGModule } from 'ng-inline-svg-2';
import { NavItemComponent } from '../nav-item/nav-item.component';
import { AgentChatButtonComponent } from '@components/chat/components/agent-chat-button/agent-chat-button';
import {
  MonogramPipe,
  PmButtonComponent,
  PmMonogramCircleComponent,
} from '@libs/ui';

@Component({
  selector: 'pm-header',
  templateUrl: './header.component.html',
  imports: [
    RouterLink,
    CdkMenuModule,
    InlineSVGModule,
    TranslateModule,
    NavItemComponent,
    NgClass,
    PmButtonComponent,
    AgentChatButtonComponent,
    NotificationPanelComponent,
    PmMonogramCircleComponent,
    MonogramPipe,
  ],
  animations: [menuTriggerAnimation],
  host: {
    class: 'w-full fixed top-0 z-[999] shadow',
  },
})
export class HeaderComponent {
  readonly userFirstName = input.required<string | undefined>();
  readonly userLastName = input.required<string | undefined>();
  readonly userEmail = input.required<string | undefined>();

  readonly profileTrigger = viewChild('profileTrigger', {
    read: CdkMenuTrigger,
  });
  readonly notificationMenuTrigger = viewChild('notificationTrigger', {
    read: CdkMenuTrigger,
  });

  protected menuStructureService = inject(MenuStructureService);
  protected notificationState = inject(NotificationStateService);
  protected bellIcon = LucideBell;

  private readonly store = inject(Store);
  protected readonly chatEnabled = this.store.selectSignal(
    isFeatureFlagEnabled(FeatureFlagId.scx_chat),
  );

  isOpened = false;
  isNotificationPanelOpen = false;

  readonly userName = computed(() =>
    `${this.userFirstName() ?? ''} ${this.userLastName() ?? ''}`.trim(),
  );

  constructor() {
    this.notificationState.initializeIfNeeded();
  }

  toggleMenu(event: Event): void {
    event.preventDefault();
    event.stopImmediatePropagation();

    if (!this.isOpened) {
      this.profileTrigger()?.close();
    } else {
      this.profileTrigger()?.open();
    }
  }

  closeMenu(): void {
    this.profileTrigger()?.close();
    this.isOpened = false;
  }

  openMenu(): void {
    this.profileTrigger()?.open();
    this.isOpened = true;
  }

  closeNotificationPanel(): void {
    this.notificationMenuTrigger()?.close();
  }
}
